import React, { useEffect, useState, useCallback } from 'react';
import { jiraAPI } from '../services/api';
import { format, startOfWeek, endOfWeek, addDays, addWeeks, subWeeks, isToday } from 'date-fns';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, Plus, Search, RefreshCw, X, Edit2, Trash2, CheckCircle, XCircle, Send } from 'lucide-react';
import toast from 'react-hot-toast';

export default function JiraLogWork() {
  // --- Core State ---
  const [config, setConfig] = useState({ enabled: false, connected: false });
  const [isAdmin, setIsAdmin] = useState(false);

  // --- Navigation State ---
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('my'); // 'my' | 'team'
  
  // --- Data State ---
  const [myWorklogs, setMyWorklogs] = useState({ totalSeconds: 0, days: [], items: [] });
  const [teamWorklogs, setTeamWorklogs] = useState({ totalSeconds: 0, byUser: [], items: [] });
  const [issueMeta, setIssueMeta] = useState({});
  
  // --- Loading State ---
  const [loadingTeam, setLoadingTeam] = useState(false);
  
  // --- Modal/Form State ---
  const [logOpen, setLogOpen] = useState(false);
  const [issuePickerOpen, setIssuePickerOpen] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [comment, setComment] = useState('');
  const [timeSpentText, setTimeSpentText] = useState('');
  const [startedDate, setStartedDate] = useState('');
  const [startedHour, setStartedHour] = useState('');
  const [startedMinute, setStartedMinute] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  
  // --- Search State ---
  const [issueSearchText, setIssueSearchText] = useState('');
  const [issueSearchResults, setIssueSearchResults] = useState([]);
  
  // --- Validation State ---
  const [valIssue, setValIssue] = useState('');
  const [valTime, setValTime] = useState('');
  const [valDate, setValDate] = useState('');

  // --- Editing State ---
  const [editingId, setEditingId] = useState(null);
  const [editMinutes, setEditMinutes] = useState('');
  const [editHours, setEditHours] = useState('');
  const [editComment, setEditComment] = useState('');

  // --- Helpers ---
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 }); // Monday
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
  const weekDays = Array.from({ length: 7 }).map((_, i) => addDays(weekStart, i));

  const getCommentText = (comment) => {
    if (!comment) return '';
    if (typeof comment === 'string') return comment;
    try {
      const stack = [comment];
      let out = '';
      while (stack.length) {
        const node = stack.shift();
        if (!node) continue;
        if (Array.isArray(node)) {
          stack.unshift(...node);
          continue;
        }
        if (node.text) out += node.text;
        if (node.content) stack.unshift(...node.content);
      }
      return out.trim();
    } catch { return ''; }
  };

  const parseTimeSpent = (input) => {
    if (!input || typeof input !== 'string') return 0;
    const s = input.trim().toLowerCase();
    if (!s) return 0;
    let total = 0;
    const re = /([0-9]*\.?[0-9]+)\s*([dhm])/g;
    let m;
    while ((m = re.exec(s)) !== null) {
      const num = parseFloat(m[1]);
      const unit = m[2];
      if (unit === 'd') total += num * 8 * 3600;
      if (unit === 'h') total += num * 3600;
      if (unit === 'm') total += num * 60;
    }
    if (total === 0 && /^[0-9]*\.?[0-9]+$/.test(s)) {
      total = parseFloat(s) * 3600;
    }
    return Math.round(total);
  };

  const formatDuration = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    if (m > 0) return `${m}m`;
    return '0m';
  };

  // --- Actions ---
  const connectJira = async () => {
    try {
      const res = await jiraAPI.getAuthUrl();
      if (res?.data?.url) window.open(res.data.url, '_blank');
    } catch (e) { toast.error('Failed to get Jira auth URL'); }
  };

  const loadMyWorklogs = useCallback(async () => {
    try {
      const params = {
        start: format(weekStart, 'yyyy-MM-dd'),
        end: format(weekEnd, 'yyyy-MM-dd')
      };
      const res = await jiraAPI.getMyWorklogs(params);
      setMyWorklogs(res.data || { totalSeconds: 0, days: [], items: [] });
      
      // Fetch meta for issues
      const ids = Array.from(new Set((res.data?.items || []).map(i => i.issueId).filter(Boolean))).slice(0, 50);
      await Promise.all(ids.map(async (id) => {
        try {
          const m = await jiraAPI.getIssueMeta(id);
          setIssueMeta(prev => {
            if (prev[id]) return prev;
            return { ...prev, [id]: m.data || {} };
          });
        } catch {}
      }));
    } catch (e) { console.error(e); }
  }, [weekStart, weekEnd]);

  const loadTeamWorklogs = useCallback(async () => {
    setLoadingTeam(true);
    try {
      const params = {
        start: format(weekStart, 'yyyy-MM-dd'),
        end: format(weekEnd, 'yyyy-MM-dd')
      };
      const res = await jiraAPI.getTeamWorklogs(params);
      setTeamWorklogs(res.data || { totalSeconds: 0, byUser: [], items: [] });

      // Fetch meta
      const ids = Array.from(new Set((res.data?.items || []).map(i => i.issueId).filter(Boolean))).slice(0, 50);
      await Promise.all(ids.map(async (id) => {
        try {
          const m = await jiraAPI.getIssueMeta(id);
          setIssueMeta(prev => {
            if (prev[id]) return prev;
            return { ...prev, [id]: m.data || {} };
          });
        } catch {}
      }));
    } catch (e) { console.error(e); }
    finally { setLoadingTeam(false); }
  }, [weekStart, weekEnd]);

  const openLogModal = (date = null, issue = null) => {
    setSelectedIssue(issue);
    setIssuePickerOpen(false);
    setComment('');
    setTimeSpentText('');
    
    const d = date || new Date();
    setStartedDate(format(d, 'yyyy-MM-dd'));
    setStartedHour(String(d.getHours()));
    setStartedMinute(String(d.getMinutes()));
    
    setValIssue('');
    setValTime('');
    setValDate('');
    setSuccess('');
    setLogOpen(true);
  };

  const submitWorklog = async () => {
    setValIssue(''); setValTime(''); setValDate('');
    if (!selectedIssue) { setValIssue('Issue is required'); return; }
    const seconds = parseTimeSpent(timeSpentText);
    if (!seconds || seconds <= 0) { setValTime('Time spent is required'); return; }
    if (!startedDate) { setValDate('Date started is required'); return; }

    setSubmitting(true);
    try {
      const pad2 = (n) => String(n).padStart(2, '0');
      const h = startedHour ? Number(startedHour) : 9;
      const m = startedMinute ? Number(startedMinute) : 0;
      const startedLocal = `${startedDate}T${pad2(h)}:${pad2(m)}`;
      
      const res = await jiraAPI.addWorklog({
        issueKey: selectedIssue.key,
        timeSpentMinutes: Math.round(seconds / 60),
        comment: comment || undefined,
        startedAt: startedLocal,
      });
      
      // Optimistic Update
      const newLogId = res.data?.worklogId || `temp-${Date.now()}`;
      const newLog = {
        id: newLogId,
        issueId: selectedIssue.id,
        timeSpentSeconds: seconds,
        started: startedLocal,
        comment: comment,
        author: { displayName: 'Me' } 
      };

      setIssueMeta(prev => ({
        ...prev,
        [selectedIssue.id]: {
          key: selectedIssue.key,
          summary: selectedIssue.fields?.summary || selectedIssue.summary
        }
      }));

      setMyWorklogs(prev => {
        const newItems = [...(prev.items || []), newLog];
        const dateKey = startedDate;
        const newDays = [...(prev.days || [])];
        const dayIndex = newDays.findIndex(d => d.date === dateKey);
        
        if (dayIndex >= 0) {
          newDays[dayIndex] = {
            ...newDays[dayIndex],
            seconds: (newDays[dayIndex].seconds || 0) + seconds
          };
        } else {
          newDays.push({ date: dateKey, seconds: seconds });
          newDays.sort((a,b) => a.date.localeCompare(b.date));
        }

        return {
          ...prev,
          totalSeconds: (prev.totalSeconds || 0) + seconds,
          days: newDays,
          items: newItems
        };
      });
      
      setSuccess('Worklog added');
      setLogOpen(false);
      loadMyWorklogs();
      if (viewMode === 'team') loadTeamWorklogs();
    } catch (e) {
      setValIssue(e?.response?.data?.message || 'Failed to add worklog');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteWorklog = async (id, issueId) => {
    if (!window.confirm('Are you sure you want to delete this worklog?')) return;
    try {
      await jiraAPI.deleteWorklog(id, issueId);
      loadMyWorklogs();
      if (viewMode === 'team') loadTeamWorklogs();
    } catch (e) { alert('Failed to delete'); }
  };

  const updateWorklog = async (id, issueId) => {
    try {
      await jiraAPI.updateWorklog(id, {
        issueId,
        timeSpentMinutes: editMinutes ? Number(editMinutes) : undefined,
        timeSpentHours: editHours ? Number(editHours) : undefined,
        comment: editComment || undefined
      });
      setEditingId(null);
      loadMyWorklogs();
      if (viewMode === 'team') loadTeamWorklogs();
    } catch (e) { alert('Failed to update'); }
  };

  // --- Effects ---
  useEffect(() => {
    (async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        setIsAdmin(user.role === 'admin');
        
        const res = await jiraAPI.getConfig();
        setConfig(res.data || { enabled: false, connected: false });
      } catch {}
    })();
  }, []);



  const handleStatusUpdate = async (id, status) => {
      try {
          await jiraAPI.updateApprovalStatus(id, status);
          toast.success(`Status updated to ${status}`);
          loadMyWorklogs();
          if (viewMode === 'team') loadTeamWorklogs();
      } catch (e) {
          toast.error('Failed to update status');
      }
  };

  useEffect(() => {
    if (config.connected) {
      if (viewMode === 'my') loadMyWorklogs();
      else loadTeamWorklogs();
    }
  }, [currentDate, viewMode, config.connected, loadMyWorklogs, loadTeamWorklogs]);

  // --- Render Helpers ---
  const renderWorklogCard = (w, showUser = false) => {
    const isEditing = editingId === w.id;
    const meta = issueMeta[w.issueId] || {};
    const seconds = Number(w.timeSpentSeconds || 0);
    const commentText = getCommentText(w.comment);
    const status = w.approvalStatus || 'PENDING';
    
    if (isEditing) {
      return (
        <div key={w.id} className="bg-white p-2 rounded shadow-sm border border-gray-200 text-xs mb-2">
           <div className="grid grid-cols-2 gap-1 mb-1">
             <input className="border rounded px-1" placeholder="Min" value={editMinutes} onChange={e=>setEditMinutes(e.target.value)} />
             <input className="border rounded px-1" placeholder="Hrs" value={editHours} onChange={e=>setEditHours(e.target.value)} />
           </div>
           <textarea className="border rounded w-full mb-1 p-1" rows={2} value={editComment} onChange={e=>setEditComment(e.target.value)} />
           <div className="flex justify-end gap-1">
             <button className="p-1 bg-gray-100 rounded" onClick={()=>setEditingId(null)}><X size={12}/></button>
             <button className="p-1 bg-green-600 text-white rounded" onClick={()=>updateWorklog(w.id, w.issueId)}><RefreshCw size={12}/></button>
           </div>
        </div>
      );
    }

    const statusColors = {
        APPROVED: 'border-l-green-500',
        REJECTED: 'border-l-red-500',
        SUBMITTED: 'border-l-blue-500',
        PENDING: 'border-l-gray-300'
    };

    const badgeColors = {
        APPROVED: 'bg-green-100 text-green-700',
        REJECTED: 'bg-red-100 text-red-700',
        SUBMITTED: 'bg-gray-100 text-gray-900',
        PENDING: 'bg-gray-100 text-gray-500'
    };

    return (
      <div key={w.id} className={`group relative bg-white p-2 rounded shadow-sm border-l-4 ${statusColors[status] || 'border-l-indigo-500'} mb-2 hover:shadow-md transition-shadow`}>
        <div className="flex justify-between items-start">
          {meta.siteUrl && meta.key ? (
            <a 
              href={`${meta.siteUrl}/browse/${meta.key}`} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="font-bold text-gray-700 text-xs hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {meta.key}
            </a>
          ) : (
            <span className="font-bold text-gray-700 text-xs">{meta.key || '...'}</span>
          )}
          <span className="font-bold text-gray-700 text-xs">{formatDuration(seconds)}</span>
        </div>
        <div className="text-xs text-gray-600 truncate mt-1" title={meta.summary}>{meta.summary || 'Loading...'}</div>
        {showUser && <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
          <div className="w-4 h-4 rounded-full bg-gray-200 overflow-hidden">
            {w.author?.avatarUrls?.['48x48'] && <img src={w.author.avatarUrls['48x48']} alt="" />}
          </div>
          <span className="truncate">{w.author?.displayName}</span>
        </div>}
        {commentText && <div className="text-xs text-gray-500 italic mt-1 truncate" title={commentText}>{commentText}</div>}
        
        <div className="flex items-center justify-between mt-2 pt-1 border-t border-gray-50">
             <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${badgeColors[status] || 'bg-gray-100'}`}>
                 {status}
             </span>
             
             <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                 {/* Submit Action for User */}
                 {status === 'PENDING' && (
                     <button onClick={(e)=>{ e.stopPropagation(); handleStatusUpdate(w.id, 'SUBMITTED'); }} className="p-0.5 text-gray-700 hover:bg-gray-100 rounded" title="Submit">
                         <Send size={12}/>
                     </button>
                 )}
                 
                 {/* Admin Actions */}
                 {isAdmin && status === 'SUBMITTED' && (
                     <>
                        <button onClick={(e)=>{ e.stopPropagation(); handleStatusUpdate(w.id, 'APPROVED'); }} className="p-0.5 text-gray-900 hover:bg-green-50 rounded" title="Approve">
                            <CheckCircle size={12}/>
                        </button>
                        <button onClick={(e)=>{ e.stopPropagation(); handleStatusUpdate(w.id, 'REJECTED'); }} className="p-0.5 text-red-600 hover:bg-red-50 rounded" title="Reject">
                            <XCircle size={12}/>
                        </button>
                     </>
                 )}
             </div>
        </div>

        <div className="absolute top-1 right-1 hidden group-hover:flex gap-1 bg-white/90 p-0.5 rounded z-10">
          <button className="p-1 text-gray-500 hover:text-gray-700" onClick={(e)=>{
            e.stopPropagation();
            setEditingId(w.id);
            setEditMinutes('');
            setEditHours((seconds/3600).toFixed(2));
            setEditComment(commentText);
          }}><Edit2 size={10}/></button>
          <button className="p-1 text-gray-500 hover:text-red-600" onClick={(e)=> { e.stopPropagation(); deleteWorklog(w.id, w.issueId); }}><Trash2 size={10}/></button>
        </div>
      </div>
    );
  };

  if (!config.enabled) return <div className="p-4 text-gray-500">Jira integration is disabled.</div>;

  return (
    <div className="flex flex-col h-full space-y-4">
      {/* --- Top Bar --- */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex items-center gap-4">
          {!config.connected ? (
             <button onClick={connectJira} className="bg-gray-900 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-800 transition-colors">Connect Jira</button>
          ) : (
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button onClick={()=>setViewMode('my')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${viewMode==='my'?'bg-white text-gray-700 shadow-sm':'text-gray-600 hover:text-gray-900'}`}>My Timesheet</button>
              <button onClick={()=>setViewMode('team')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${viewMode==='team'?'bg-white text-gray-700 shadow-sm':'text-gray-600 hover:text-gray-900'}`}>Team Timesheet</button>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-2">
           <button onClick={()=>setCurrentDate(subWeeks(currentDate, 1))} className="p-1.5 hover:bg-gray-100 rounded-md text-gray-600"><ChevronLeft size={20}/></button>
           <div className="flex items-center gap-2 px-2">
             <CalendarIcon size={18} className="text-gray-500"/>
             <span className="text-sm font-medium text-gray-900 w-40 text-center">
               {format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}
             </span>
           </div>
           <button onClick={()=>setCurrentDate(addWeeks(currentDate, 1))} className="p-1.5 hover:bg-gray-100 rounded-md text-gray-600"><ChevronRight size={20}/></button>
           <button onClick={()=>setCurrentDate(new Date())} className="ml-2 text-xs font-medium text-gray-700 hover:text-gray-700">Today</button>
        </div>

        <div className="flex items-center gap-2">

          <button onClick={()=>openLogModal()} className="flex items-center gap-1.5 bg-gray-900 text-white px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-800 transition-colors shadow-sm">
            <Plus size={16}/>
            <span>Log Work</span>
          </button>
        </div>
      </div>

      {/* --- Main Content --- */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden flex-1">
        
        {/* Week Calendar Header */}
        <div className="grid grid-cols-7 border-b bg-gray-50 divide-x">
          {weekDays.map((day) => {
             const dateKey = format(day, 'yyyy-MM-dd');
             const isTodayDate = isToday(day);
             
             // Calculate daily total
             let dayTotal = 0;
             if (viewMode === 'my') {
               const dayData = myWorklogs.days.find(d => d.date === dateKey);
               dayTotal = dayData ? dayData.seconds : 0;
             } else {
               // For team view, sum all users for this day? Or just leave generic?
               // In Matrix view, the header total might be less relevant, but we can sum everyone.
               teamWorklogs.byUser.forEach(u => {
                 const logs = u.items.filter(i => i.started && i.started.startsWith(dateKey));
                 logs.forEach(l => dayTotal += (l.timeSpentSeconds || 0));
               });
             }
             
             return (
               <div key={dateKey} className={`p-3 text-center ${isTodayDate ? 'bg-gray-100' : ''}`}>
                 <div className={`text-xs font-semibold uppercase mb-1 ${isTodayDate ? 'text-gray-700' : 'text-gray-500'}`}>
                   {format(day, 'EEE')}
                 </div>
                 <div className={`text-sm font-semibold mb-1 ${isTodayDate ? 'text-gray-700' : 'text-gray-900'}`}>
                   {format(day, 'd')}
                 </div>
                 <div className={`text-xs font-medium px-2 py-0.5 rounded-full inline-block ${dayTotal > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                   {formatDuration(dayTotal)}
                 </div>
               </div>
             );
          })}
        </div>

        {/* Week Calendar Grid */}
        {viewMode === 'my' ? (
           <div className="grid grid-cols-7 divide-x min-h-[500px]">
             {weekDays.map((day) => {
               const dateKey = format(day, 'yyyy-MM-dd');
               const dayLogs = (myWorklogs.items || []).filter(w => {
                 if (!w.started) return false;
                 return w.started.startsWith(dateKey);
               });
               
               return (
                 <div 
                   key={dateKey} 
                   className="p-2 hover:bg-gray-50 transition-colors group flex flex-col relative cursor-pointer"
                   onClick={(e) => {
                     // Prevent opening if clicking inside a card or button
                     if (e.target.closest('.worklog-card') || e.target.closest('button')) return;
                     openLogModal(day);
                   }}
                 >
                   <div className="mb-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button 
                        onClick={(e) => { e.stopPropagation(); openLogModal(day); }}
                        className="w-full py-1 rounded bg-gray-100 text-gray-700 hover:bg-gray-100 text-xs font-medium flex items-center justify-center gap-1"
                      >
                        <Plus size={12}/> Log Time
                      </button>
                   </div>
                   
                   <div className="flex-1 space-y-2">
                     {dayLogs.map(w => (
                       <div key={w.id} className="worklog-card">
                         {renderWorklogCard(w)}
                       </div>
                     ))}
                   </div>
                   
                   {/* Daily Total Footer */}
                   <div className="mt-auto pt-2 border-t border-gray-100 text-center">
                     <span className="text-xs font-bold text-gray-500">Total: </span>
                     <span className="text-xs font-bold text-gray-700">
                        {formatDuration(dayLogs.reduce((acc, w) => acc + (Number(w.timeSpentSeconds)||0), 0))}
                     </span>
                   </div>
                 </div>
               );
             })}
           </div>
        ) : (
          // Team Matrix View
          <div className="overflow-x-auto">
            {loadingTeam && <div className="p-4 text-center text-gray-500">Loading team data...</div>}
            {!loadingTeam && (
              <div className="min-w-full">
                {/* Re-implementing Team View correctly: User Name Row + 7 Day Grid */}
                <div className="divide-y">
                  {teamWorklogs.byUser.map((user) => (
                    <div key={user.accountId} className="group">
                      <div className="bg-gray-50 px-4 py-2 text-sm font-bold text-gray-800 flex justify-between items-center border-b border-gray-100">
                        <div className="flex items-center gap-2">
                           <div className="w-6 h-6 rounded-full bg-gray-300 overflow-hidden">
                             {user.avatar && <img src={user.avatar} alt="" />}
                           </div>
                           {user.displayName}
                        </div>
                        <div className="flex items-center gap-2">
                           <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">Weekly Total</span>
                           <span className="text-sm font-bold bg-gray-100 text-gray-700 px-2.5 py-1 rounded-full">
                             {formatDuration(user.totalSeconds)}
                           </span>
                        </div>
                      </div>
                      <div className="grid grid-cols-7 divide-x">
                        {weekDays.map((day) => {
                          const dateKey = format(day, 'yyyy-MM-dd');
                          const userDayLogs = user.items.filter(w => w.started && w.started.startsWith(dateKey));
                          const dayTotal = userDayLogs.reduce((acc, curr) => acc + (Number(curr.timeSpentSeconds)||0), 0);
                          
                          return (
                            <div key={dateKey} className="p-2 min-h-[80px] hover:bg-gray-50">
                              {dayTotal > 0 && (
                                <div className="mb-2 text-right">
                                   <span className="text-xs font-bold text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded">{formatDuration(dayTotal)}</span>
                                </div>
                              )}
                              <div className="space-y-1">
                                {userDayLogs.map(w => {
                                  const meta = issueMeta[w.issueId] || {};
                                  return (
                                    <div key={w.id} className="text-[10px] bg-white border rounded p-1 shadow-sm truncate" title={`${meta.key} - ${meta.summary}`}>
                                      <span className="font-medium text-gray-700 mr-1">{meta.key}</span>
                                      <span className="text-gray-600">{formatDuration(w.timeSpentSeconds)}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  {teamWorklogs.byUser.length === 0 && <div className="p-8 text-center text-gray-500">No team activity found for this week.</div>}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* --- Log Work Modal --- */}
      {logOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-0">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={()=>setLogOpen(false)}></div>
          <div className="relative bg-white rounded-md w-full max-w-md shadow-2xl flex flex-col max-h-[90vh]">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Log Work</h3>
              <button onClick={()=>setLogOpen(false)} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
            </div>
            
            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              {/* Issue Selector */}
              <div className="space-y-1.5">
                <button
                  type="button"
                  onClick={() => setIssuePickerOpen((open) => !open)}
                  className="w-full flex items-center justify-between text-sm font-medium text-gray-700"
                >
                  <span>Issue</span>
                  <span className="text-xs font-normal text-gray-500">
                    {selectedIssue ? selectedIssue.key : issuePickerOpen ? 'Hide' : 'Choose'}
                  </span>
                </button>
                {selectedIssue ? (
                  <div className="flex items-center justify-between p-2 bg-gray-100 border border-gray-200 rounded-md">
                    <div className="flex items-center gap-2 overflow-hidden">
                      <span className="text-xs font-bold text-gray-700 bg-white px-1.5 py-0.5 rounded border border-gray-200">{selectedIssue.key}</span>
                      <span className="text-sm text-gray-700 truncate">{selectedIssue.fields?.summary || selectedIssue.summary}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedIssue(null);
                        setIssuePickerOpen(true);
                      }}
                      className="text-xs text-gray-700 hover:text-gray-700 font-medium px-2"
                    >
                      Change
                    </button>
                  </div>
                ) : issuePickerOpen ? (
                  <div className="relative">
                    <div className="relative">
                      <Search className="absolute left-3 top-2.5 text-gray-400" size={16}/>
                      <input
                        className="w-full pl-9 pr-4 py-2 border rounded-md focus:ring-2 focus:ring-gray-200 focus:border-gray-200 outline-none transition-all"
                        placeholder="Search issues (e.g. PROJ-123)"
                        value={issueSearchText}
                        onChange={async (e) => {
                          const v = e.target.value;
                          setIssueSearchText(v);
                          try {
                            const res = await jiraAPI.getIssues(v);
                            setIssueSearchResults(res.data.issues || []);
                          } catch {}
                        }}
                      />
                    </div>
                    {issueSearchResults.length > 0 && (
                      <ul className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {issueSearchResults.map(i => (
                          <li key={i.id} onClick={()=>{setSelectedIssue(i); setIssueSearchText(''); setIssueSearchResults([]);}} className="p-2 hover:bg-gray-100 cursor-pointer flex flex-col border-b last:border-0">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-sm text-gray-900">{i.key}</span>
                            </div>
                            <span className="text-xs text-gray-500 truncate">{i.fields?.summary || i.summary}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {valIssue && <p className="text-red-500 text-xs mt-1">{valIssue}</p>}
                  </div>
                ) : (
                  <div className="rounded-md border border-dashed border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
                    Issue picker is collapsed. Click Issue to choose or change the linked issue.
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-gray-700">Time Spent</label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-2.5 text-gray-400" size={16}/>
                    <input
                      className="w-full pl-9 pr-4 py-2 border rounded-md focus:ring-2 focus:ring-gray-200 focus:border-gray-200 outline-none"
                      placeholder="e.g. 1h 30m"
                      value={timeSpentText}
                      onChange={e=>{setTimeSpentText(e.target.value); setValTime('');}}
                    />
                  </div>
                  {valTime && <p className="text-red-500 text-xs">{valTime}</p>}
                </div>
                
                <div className="space-y-1.5">
                   <label className="text-sm font-medium text-gray-700">Date</label>
                   <input
                     type="date"
                     className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-gray-200 focus:border-gray-200 outline-none"
                     value={startedDate}
                     onChange={e=>{setStartedDate(e.target.value); setValDate('');}}
                   />
                   {valDate && <p className="text-red-500 text-xs">{valDate}</p>}
                </div>
              </div>

              <div className="space-y-1.5">
                 <label className="text-sm font-medium text-gray-700">Comment</label>
                 <textarea
                   className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-gray-200 focus:border-gray-200 outline-none"
                   rows={4}
                   placeholder="What did you work on?"
                   value={comment}
                   onChange={e=>setComment(e.target.value)}
                 />
              </div>
              
              {success && <div className="bg-green-50 text-green-700 p-3 rounded-md text-sm">{success}</div>}
            </div>

            <div className="p-4 border-t bg-gray-50 flex justify-end gap-3 rounded-b-xl">
              <button onClick={()=>setLogOpen(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900">Cancel</button>
              <button 
                onClick={submitWorklog}
                disabled={submitting}
                className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-md hover:bg-gray-800 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {submitting && <RefreshCw className="animate-spin" size={16}/>}
                {submitting ? 'Saving...' : 'Save Worklog'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
